import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { chevronLeftIcon, chevronRightIcon } from '@progress/kendo-svg-icons';
import { Interval } from 'date-fns';
import { Observable, shareReplay } from 'rxjs';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../core/constants-repository/project-calculation.constants';
import { SharedTaskHandlerService } from '../../../../core/services/features/shared-task-handling/shared-task-handler.service';
import { TimeUnitOptionLiterals } from '../../../../core/text-db/units.text';
import { DropdownOption } from '../../../../shared/data-types/dropdown-option';
import { TimeBlockWithTask } from '../../../../shared/data-types/shared-time-block-types';
import { FeeUnit, TimeUnits } from '../../../../shared/data-types/unit-types';
import { BudgetCalculationService } from '../project-edit/manage-project/project-calculation-form/services/budget-calculation.service';
import { ProjectDataService } from '../state-and-data-handling/project-data.service';
import { ProjectProgressChartService } from './services/project-progress-chart.service';

@Component({
  selector: 'app-project-progress',
  templateUrl: './project-progress.component.html',
  styleUrl: './project-progress.component.scss',
  standalone: false,
})
export class ProjectProgressComponent implements OnInit {
  // Signals
  budgetType = signal<BudgetOptions>(null);
  expenditureType = signal<ExpenditureOptions>(null);
  feeUnit = signal<FeeUnit>(null);
  budgetChartVisible = computed(() => {
    // Hide budget chart if no budget is set or if expenditure unit is hours but budget unit is monetary.
    return (
      this.budgetType() !== BudgetOptions.NoBudget &&
      (this.expenditureType() !== ExpenditureOptions.ExpendituresInHours ||
        this.feeUnit() !== 'monetary')
    );
  });
  taskBudgetVisible = computed(() => {
    return (
      this.budgetType() === BudgetOptions.MonetaryPerTask ||
      this.budgetType() === BudgetOptions.HoursPerTask
    );
  });

  public timeBlocksWithTask$: Observable<readonly TimeBlockWithTask[]> = null;
  public readonly chevronRightIcon = chevronRightIcon;
  public readonly chevronLeftIcon = chevronLeftIcon;
  public readonly viewOptions: DropdownOption[] = [
    { text: TimeUnitOptionLiterals.get(TimeUnits.Month), value: TimeUnits.Month },
    { text: TimeUnitOptionLiterals.get(TimeUnits.Year), value: TimeUnits.Year },
  ];

  public timeRange: Interval = null;
  // Constants
  public readonly TimeUnits = TimeUnits;

  public readonly BudgetOptions = BudgetOptions;
  // DI
  public readonly projectProgressChartService = inject(ProjectProgressChartService);
  private readonly sharedTaskHandlerService = inject(SharedTaskHandlerService);
  private readonly projectDataService = inject(ProjectDataService);

  constructor() {
    // Effect to re-fetch data when date range changes
    effect(() => {
      const chartModel = this.projectProgressChartService.chartModel();
      if (chartModel.startDate && chartModel.endDate) {
        this.timeRange = {
          start: chartModel.startDate,
          end: chartModel.endDate,
        };
      }
    });
  }

  ngOnInit(): void {
    this.fetchTimeBlocksWithTasks();
  }

  private fetchTimeBlocksWithTasks(): void {
    const projectClone = this.projectDataService.getProjectClone();
    this.budgetType.set(projectClone.budgetType);
    this.expenditureType.set(projectClone.expenditureType);
    this.feeUnit.set(BudgetCalculationService.getFeeUnit(projectClone));
    this.timeBlocksWithTask$ = this.sharedTaskHandlerService
      .fetchTimeBlocksWithTasks(projectClone, true, null, false, null)
      .pipe(shareReplay(1));
  }
}
